import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import './Creators.css';

const Creators = () => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCreatorUserId, setNewCreatorUserId] = useState('');
  const [isAddingCreator, setIsAddingCreator] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState(null);

  useEffect(() => {
    loadCreators();
  }, []);

  const loadCreators = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query users with subscriptionSource === "organization" AND isCreator === true
      const usersRef = collection(db, 'users');
      const creatorsQuery = query(
        usersRef,
        where('subscriptionSource', '==', 'organization'),
        where('isCreator', '==', true)
      );
      const creatorsSnapshot = await getDocs(creatorsQuery);

      const creatorsData = [];
      creatorsSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        creatorsData.push({
          userId: userDoc.id,
          email: userData.email || 'N/A',
          displayName: userData.displayName || userData.name || 'N/A',
          profilePicture: userData.profilePicture || userData.photoURL || null,
          school: userData.school || userData.schoolName || 'N/A',
          hasCompletedOnboarding: userData.hasCompletedOnboarding || false,
          subscriptionStatus: userData.subscriptionStatus || 'N/A',
          createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : (userData.createdAt instanceof Date ? userData.createdAt : null),
        });
      });

      // Sort by display name
      creatorsData.sort((a, b) => a.displayName.localeCompare(b.displayName));

      setCreators(creatorsData);
    } catch (err) {
      console.error('Error loading creators:', err);
      setError('Failed to load creators. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCreator = async () => {
    if (!newCreatorUserId.trim()) {
      alert('Please enter a user ID');
      return;
    }

    try {
      setIsAddingCreator(true);
      setError(null);

      // Check if user exists
      const userDocRef = doc(db, 'users', newCreatorUserId.trim());
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        alert('User not found with that ID');
        setIsAddingCreator(false);
        return;
      }

      const userData = userDoc.data();

      // Update user document to add creator status
      await updateDoc(userDocRef, {
        subscriptionSource: 'organization',
        isCreator: true,
        subscriptionStatus: 'active', // Set active status
      });

      alert('Creator added successfully!');
      setNewCreatorUserId('');
      await loadCreators(); // Reload the list
    } catch (err) {
      console.error('Error adding creator:', err);
      setError('Failed to add creator. Please try again.');
      alert('Error adding creator: ' + (err.message || 'Unknown error'));
    } finally {
      setIsAddingCreator(false);
    }
  };

  const handleRevokeCreator = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this creator\'s subscription? This will remove their organization subscription, creator status, and subscription status.')) {
      return;
    }

    try {
      setRevokingUserId(userId);
      setError(null);

      const userDocRef = doc(db, 'users', userId);

      // Remove organization subscription, isCreator, and status
      await updateDoc(userDocRef, {
        subscriptionSource: deleteField(),
        isCreator: deleteField(),
        subscriptionStatus: deleteField(),
      });

      alert('Creator subscription revoked successfully!');
      await loadCreators(); // Reload the list
    } catch (err) {
      console.error('Error revoking creator:', err);
      setError('Failed to revoke creator. Please try again.');
      alert('Error revoking creator: ' + (err.message || 'Unknown error'));
    } finally {
      setRevokingUserId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getOnboardingStatus = (hasCompletedOnboarding) => {
    return hasCompletedOnboarding ? 'completed' : 'in_progress';
  };

  const getOnboardingStatusColor = (hasCompletedOnboarding) => {
    return hasCompletedOnboarding ? '#4caf50' : '#ff9800';
  };

  if (loading) {
    return (
      <div className="creators-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading creators...</p>
        </div>
      </div>
    );
  }

  if (error && !creators.length) {
    return (
      <div className="creators-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadCreators} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="creators-container">
      <div className="creators-header">
        <div>
          <h1>Creators</h1>
          <p className="creators-subtitle">Users with organization subscription and creator status</p>
        </div>
        <button onClick={loadCreators} className="btn-refresh" disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {/* Add Creator Section */}
      <div className="add-creator-section">
        <h2>Add Creator</h2>
        <div className="add-creator-form">
          <input
            type="text"
            placeholder="Enter User ID"
            value={newCreatorUserId}
            onChange={(e) => setNewCreatorUserId(e.target.value)}
            className="user-id-input"
            disabled={isAddingCreator}
          />
          <button
            onClick={handleAddCreator}
            className="btn-add-creator"
            disabled={isAddingCreator || !newCreatorUserId.trim()}
          >
            {isAddingCreator ? '⏳ Adding...' : '➕ Add Creator'}
          </button>
        </div>
        {error && <p className="error-message">{error}</p>}
      </div>

      {/* Creators List */}
      <div className="creators-stats">
        <div className="stat-card">
          <div className="stat-value">{creators.length}</div>
          <div className="stat-label">Total Creators</div>
        </div>
      </div>

      <div className="creators-table-container">
        <table className="creators-table">
          <thead>
            <tr>
              <th>Profile</th>
              <th>Display Name</th>
              <th>Email</th>
              <th>User ID</th>
              <th>School</th>
              <th>Onboarding Status</th>
              <th>Subscription Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {creators.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state">
                  No creators found
                </td>
              </tr>
            ) : (
              creators.map((creator) => (
                <tr key={creator.userId}>
                  <td>
                    {creator.profilePicture ? (
                      <img
                        src={creator.profilePicture}
                        alt={creator.displayName}
                        className="profile-picture"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="profile-picture-placeholder"
                      style={{ display: creator.profilePicture ? 'none' : 'flex' }}
                    >
                      {creator.displayName.charAt(0).toUpperCase()}
                    </div>
                  </td>
                  <td>
                    <strong>{creator.displayName}</strong>
                  </td>
                  <td>{creator.email}</td>
                  <td>
                    <code className="user-id-code">{creator.userId}</code>
                  </td>
                  <td>{creator.school}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getOnboardingStatusColor(creator.hasCompletedOnboarding) }}
                    >
                      {getOnboardingStatus(creator.hasCompletedOnboarding)}
                    </span>
                  </td>
                  <td>
                    <span className="status-badge" style={{ backgroundColor: '#238a94' }}>
                      {creator.subscriptionStatus}
                    </span>
                  </td>
                  <td>{formatDate(creator.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => handleRevokeCreator(creator.userId)}
                      className="btn-revoke"
                      disabled={revokingUserId === creator.userId}
                    >
                      {revokingUserId === creator.userId ? '⏳ Revoking...' : '🗑️ Revoke'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Creators;
